export interface StorageInstance extends Omit<Storage, 'getItem' | 'setItem'> {
  getItem(key: string): any;
  setItem(key: string, value: any): any;
}

export type IsPromise<T extends (...args: any) => any, R = any> =
  ReturnType<T> extends Promise<any> ? Promise<R> : R;

export class MyStorage<T extends StorageInstance> {
  private _storage: T;
  private _module: string;
  private _encode: boolean;
  constructor(storage: T, module: string = '', encode = true) {
    this._storage = storage;
    this._module = module;
    this._encode = encode;
  }
  private _encodeData(data: any) {
    return this._encode ? JSON.stringify([data]) : data;
  }
  private _decodeData(data: any) {
    if (this._encode) {
      try {
        return JSON.parse(data)[0];
      } catch (_e) {
        return null;
      }
    } else {
      return data;
    }
  }
  getKey(key: string) {
    const { _module } = this;
    return `${_module && _module + ':'}${key}`;
  }
  setItem(key: string, value: any): IsPromise<T['setItem']> {
    return this._storage.setItem(this.getKey(key), this._encodeData(value));
  }
  getItem<R = any>(key: string): IsPromise<T['getItem'], R> {
    const result = this._storage.getItem(this.getKey(key));
    return result instanceof Promise
      ? result.then((res) => this._decodeData(res))
      : this._decodeData(result);
  }
  removeItem(key: string): IsPromise<T['removeItem']> {
    return this._storage.removeItem(this.getKey(key));
  }
  clear(): IsPromise<T['clear']> {
    return this._storage.clear();
  }
  getModule(name: string) {
    return new MyStorage(this._storage, name, this._encode);
  }
  async clearModule() {
    const {
      _module,
      _storage,
      _storage: { length, key },
    } = this;
    if (_module && length && Boolean(key)) {
      const prefix = this.getKey('');
      const reg = new RegExp(`^${prefix}`);
      const keys: string[] = [];
      for (let i = 0; i < length; i++) {
        const key = _storage.key(i)!;
        if (reg.test(key)) {
          keys.push(key);
        }
      }
      await Promise.all(keys.map((key) => _storage.removeItem(key)));
    }
  }
}
